import { calculateTicketedBookingTotal } from "utils/widgetBookingPricing"
import { countTickets } from "utils/ticketedBooking"
import { getBookableTicketTypes } from "pagesV2/BookingWidget/helpers"
import { numberToCurrency } from "helpers/string"
import { getPowerTranzCardFee } from "helpers/powertranz"
import { grossUpCardFee, roundToCents } from "utils/bookingCalculations"

// The widget's breakdown in the shape New Booking's summaries read (splitPriceBreakdown): a line per
// ticket type ("Adults x 4"), then one Taxes & Fees entry holding the fees the guide lists on their
// own line. Add-ons are left out; the summaries list them from the selection's addOns.
const CARD_OR_LINK_PAYMENTS = ["card", "link", "paypalLink"]
// Charged through PowerTranz: grossed up with the guide's own PowerTranz rate (no fixed part) instead
// of the Stripe fee entry, as calculateBookingTotal does for a private booking.
const POWERTRANZ_PAYMENTS = ["powertranzCard", "powertranzLink"]

const toBookingBreakdown = (breakdown) => {
  const ticketLines = breakdown
    .filter((line) => line.kind === "ticket")
    .map(({ label, amount, value }) => ({ label, amount, rawAmount: value }))
  const listedFees = breakdown
    .filter((line) => line.kind === "fee")
    .map(({ label, amount, value }) => ({ label, amount, rawAmount: value, isLineItem: true }))
  const feesTotal = breakdown
    .filter((line) => line.kind === "fee" || line.kind === "tax_and_fees")
    .reduce((sum, line) => sum + line.value, 0)
  // Labelled as calculateBookingTotal labels it, for Edit Booking's PriceBreakdown.
  const taxEntry = { label: "Tax & Fees", amount: numberToCurrency(feesTotal), rawAmount: feesTotal, isTaxFee: true, children: listedFees }
  return feesTotal > 0 ? [...ticketLines, taxEntry] : ticketLines
}

/**
 * A ticketed selection's price in the fields New Booking keeps on bookingSelections, as
 * calculateBookingTotal's callers store a private one. There is no guide-side ticketed pricing yet
 * (guide-orders-backend.md, G6), so it is the guest widget's calculator, which matches the API for
 * widget bookings. Null until a ticket is chosen.
 *
 * @param {object} params
 * @param {object} params.experience       the experience detail (ticket_types, add_ons, fees, adjustments)
 * @param {object} params.selections       bookingSelections: date, ticketCounts, addOns
 * @param {Array}  params.discountPromos   applied promo codes
 * @param {number} params.depositFraction  share of the pre-card total paid by card now
 * @param {string} params.paymentType      the guide's payment method on step 2 (card, link, paypalLink, cash)
 * @param {number} params.overrideGuestSubtotal  the guide's adjusted subtotal for the tickets, if any
 */
export const priceTicketedSelection = ({ experience, selections, discountPromos = [], depositFraction = 1, paymentType, overrideGuestSubtotal }) => {
  const ticketTypes = getBookableTicketTypes(experience)
  if (!selections?.date || countTickets(ticketTypes, selections.ticketCounts) === 0) return null
  // The widget is always paid by card. A guide's booking carries the platform fee unless paid in
  // cash, so step 1 (no payment method yet) quotes it as a private one does in calculateBookingTotal;
  // the card fee only when paid by card or link.
  const isCardOrLink = CARD_OR_LINK_PAYMENTS.includes(paymentType)
  const shared = {
    ticketTypes,
    ticketCounts: selections.ticketCounts,
    date: selections.date,
    addOnSelections: selections.addOns || {},
    tripAddOns: experience?.add_ons || [],
    customFees: (experience?.assigned_custom_fees || []).filter((fee) => paymentType !== "cash" || fee?.title !== "Platform Fee"),
    discountPromos,
    cardFeePassthroughEnabled: isCardOrLink && experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    depositFraction,
    isCardFeeListed: false,
    overrideGuestSubtotal
  }
  const dateAdjustments = {
    dayAdjustments: experience?.day_adjustments,
    seasonalAdjustments: experience?.seasonal_adjustments,
    availableDateRanges: experience?.available_date_ranges
  }
  const adjusted = calculateTicketedBookingTotal({ ...shared, ...dateAdjustments })
  const original = calculateTicketedBookingTotal(shared)
  // The tickets' own price on the date, which an adjusted subtotal replaces (as originalBaseAmount
  // is for a private booking).
  const ticketsSubtotal =
    overrideGuestSubtotal == null
      ? adjusted.guestSubtotal
      : calculateTicketedBookingTotal({ ...shared, ...dateAdjustments, overrideGuestSubtotal: undefined }).guestSubtotal
  const powerTranzCardFee = POWERTRANZ_PAYMENTS.includes(paymentType)
    ? roundToCents(grossUpCardFee(adjusted.preCardTotal * depositFraction, getPowerTranzCardFee(experience?.platform_fee_setting)))
    : 0
  return {
    amount: adjusted.total + powerTranzCardFee,
    baseAmount: adjusted.guestSubtotal,
    originalBaseAmount: ticketsSubtotal,
    breakdown: toBookingBreakdown(adjusted.breakdown),
    preCardTotal: adjusted.preCardTotal,
    preCardTotalBeforeAdjustments: original.preCardTotal,
    cardProcessingFee: adjusted.total - adjusted.preCardTotal + powerTranzCardFee
  }
}
